import { useTranslation } from 'react-i18next';
import React, { useState, useMemo } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { X, Plus, History, User, AlertTriangle } from 'lucide-react';
import TeamMemberRateHistory from './TeamMemberRateHistory';
import CopyableLabel, { AddressActions } from './shared/CopyableLabel';
import BulkAddTeamDialog from './BulkAddTeamDialog';
import { findAssignmentConflicts } from '../lib/projectAssignments';
import { FloatingSaveButton } from './FloatingSaveButton';
import usePermissions from '../hooks/usePermissions';

const ContactPersonFields = ({ label, contact, onChange, customerContacts = [] }) => {
  const { t } = useTranslation();
  const [openSuggestions, setOpenSuggestions] = useState(false);
  // Case-insensitive prefix + substring match on the customer's saved
  // contacts. Empty query → show all so users can browse.
  const suggestions = useMemo(() => {
    if (!customerContacts?.length) return [];
    const q = (contact.name || '').trim().toLowerCase();
    if (!q) return customerContacts.slice(0, 8);
    return customerContacts
      .filter(c => (c.name || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [customerContacts, contact.name]);
  const applySuggestion = (c) => {
    onChange({ name: c.name || '', position: c.position || '', phone: c.phone || '', email: c.email || '' });
    setOpenSuggestions(false);
  };

  return (
  <Card className="mb-4">
    <CardHeader>
      <CardTitle className="text-sm">{label}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="relative">
          <CopyableLabel label="Name" value={contact.name} testId={`copy-project-contact-${label.replace(/\s+/g,'-').toLowerCase()}-name`} />
          <Input
            placeholder={t('auto.proj.fullName')}
            value={contact.name || ''}
            onChange={(e) => { onChange({ ...contact, name: e.target.value }); setOpenSuggestions(true); }}
            onFocus={() => setOpenSuggestions(true)}
            onBlur={() => setTimeout(() => setOpenSuggestions(false), 150)}
            data-testid={`project-contact-${label.replace(/\s+/g,'-').toLowerCase()}-name`}
          />
          {openSuggestions && suggestions.length > 0 && (
            <div className="absolute z-10 mt-1 w-full max-h-52 overflow-auto rounded-md border bg-white shadow-lg" data-testid={`contact-suggestions-${label.replace(/\s+/g,'-').toLowerCase()}`}>
              {suggestions.map((c, i) => (
                <button key={i} type="button"
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 flex items-start gap-2"
                  onMouseDown={(e) => { e.preventDefault(); applySuggestion(c); }}
                  data-testid={`contact-suggestion-${i}`}>
                  <User className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.name || '—'}</div>
                    {c.position && <div className="text-xs text-slate-500 truncate">{c.position}</div>}
                    <div className="text-xs text-slate-400 truncate">{[c.email, c.phone].filter(Boolean).join(' · ')}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <CopyableLabel label="Position" value={contact.position} testId={`copy-project-contact-${label.replace(/\s+/g,'-').toLowerCase()}-position`} />
          <Input
            placeholder={t('auto.proj.positionTitle')}
            value={contact.position || ''}
            onChange={(e) => onChange({ ...contact, position: e.target.value })}
          />
        </div>
        <div>
          <CopyableLabel label="Phone" value={contact.phone} testId={`copy-project-contact-${label.replace(/\s+/g,'-').toLowerCase()}-phone`} />
          <Input
            placeholder={t('auto.proj.phoneNumber')}
            value={contact.phone || ''}
            onChange={(e) => onChange({ ...contact, phone: e.target.value })}
          />
        </div>
        <div>
          <CopyableLabel label="Email" value={contact.email} testId={`copy-project-contact-${label.replace(/\s+/g,'-').toLowerCase()}-email`} />
          <Input
            placeholder={t('auto.proj.emailAddress')}
            type="email"
            value={contact.email || ''}
            onChange={(e) => onChange({ ...contact, email: e.target.value })}
          />
        </div>
      </div>
    </CardContent>
  </Card>
  );
};

const ProjectForm = ({ 
  projectForm, 
  setProjectForm, 
  customers, 
  employees,
  settings,
  currentUser,
  onSubmit, 
  isEdit = false,
  otherProjects = [],
}) => {
  const { t } = useTranslation();
  // Contacts and Team & Rates are privileged: crew see Basic Info and
  // Location only. The server strips the data too — this just stops the
  // app offering tabs that would come back empty.
  const { can } = usePermissions();
  const seeContacts = can('projects.view_contacts');
  const seeTeam = can('projects.view_team');
  const isAdminOrManager = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  const isAdmin = currentUser?.role === 'admin';
  const currencies = settings?.currencies || ['EUR', 'PLN', 'GBP', 'USD'];
  // Rate history modal control. Tracks which team-member row is open.
  // null = closed.
  const [rateHistoryFor, setRateHistoryFor] = useState(null); // { index, employee_id, employee_name }
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  // Get project roles from settings with fallback defaults
  const projectRoles = settings?.project_roles || [
    'Mechanic', 'Electrician', 'ME Team Leader', 'EL Team Leader',
    'Site Manager', 'Foreman', 'Plumber', 'HVAC Technician',
    'Welder', 'Carpenter', 'Project Engineer', 'Quality Inspector'
  ];

  const handleContactChange = (contactType, updatedContact) => {
    setProjectForm(prev => ({ ...prev, [contactType]: updatedContact }));
  };

  const addTeamMember = () => {
    setProjectForm(prev => ({
      ...prev,
      team_members: [...(prev.team_members || []), { 
        employee_id: '', 
        role: '',
        hourly_rate: '',
        currency: 'EUR'
      }]
    }));
  };

  // Bulk-append multiple employees at once, mirroring the same
  // "autofill from primary position / rate / currency" logic that
  // `updateTeamMember` applies for a single-row employee pick. We do
  // it in one setState so all rows land in a single re-render (a
  // sequential loop would race on stale snapshots).
  const bulkAddMembers = (emps) => {
    setProjectForm((prev) => {
      const existing = prev.team_members || [];
      const existingIds = new Set(existing.map((m) => m.employee_id).filter(Boolean));
      const newRows = emps
        .filter((e) => !existingIds.has(e.id))
        .map((e) => ({
          employee_id: e.id,
          role: e.position_1 || e.position || '',
          hourly_rate: (e.hourly_rate_1 ?? e.hourly_rate ?? '') || '',
          currency: e.currency_1 || 'EUR',
        }));
      return { ...prev, team_members: [...existing, ...newRows] };
    });
  };

  const removeTeamMember = (index) => {
    setProjectForm(prev => ({
      ...prev,
      team_members: (prev.team_members || []).filter((_, i) => i !== index)
    }));
  };

  const updateTeamMember = (index, field, value) => {
    setProjectForm((prev) => {
      const updated = [...(prev.team_members || [])];
      let next = { ...updated[index], [field]: value };
      // Autofill role + hourly rate/currency from the selected
      // employee's primary position + rate when the row is empty.
      if (field === 'employee_id' && value) {
        const emp = (employees || []).find((e) => e.id === value);
        if (emp) {
          if (!next.role) {
            next.role = emp.position_1 || emp.position || '';
          }
          const hasRate = next.hourly_rate !== '' && next.hourly_rate !== null && next.hourly_rate !== undefined;
          if (!hasRate) {
            const rate = emp.hourly_rate_1 ?? emp.hourly_rate ?? null;
            if (rate !== null && rate !== undefined && rate !== 0) {
              next.hourly_rate = rate;
            }
            const cur = emp.currency_1 || null;
            if (cur) next.currency = cur;
          }
        }
      }
      updated[index] = next;
      return { ...prev, team_members: updated };
    });
  };

  // Atomic multi-field update for a team member. Used by the rate
  // history modal which needs to refresh `hourly_rate`, `currency`
  // and `rate_history` together — calling updateTeamMember three
  // times in a row would race on stale `projectForm` snapshots.
  const patchTeamMember = (index, patch) => {
    setProjectForm((prev) => {
      const updated = [...(prev.team_members || [])];
      updated[index] = { ...updated[index], ...patch };
      return { ...prev, team_members: updated };
    });
  };

  // Ensure contact objects exist, defaulting to empty objects if null/undefined
  const getContact = (contactType) => {
    return projectForm[contactType] || { name: '', position: '', phone: '', email: '' };
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="basic" className="px-3 py-2" data-testid="tab-basic">{t('auto.proj.basicInfo')}</TabsTrigger>
          <TabsTrigger value="location" className="px-3 py-2" data-testid="tab-location">{t('auto.proj.location')}</TabsTrigger>
          {seeContacts && (
            <TabsTrigger value="contacts" className="px-3 py-2" data-testid="tab-contacts">{t('auto.proj.contacts')}</TabsTrigger>
          )}
          {seeTeam && (
            <TabsTrigger value="team" className="px-3 py-2" data-testid="tab-team">{t('auto.proj.teamRates')}</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="basic" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <CopyableLabel label={t('auto.proj.projectNumber')} value={projectForm.project_number} testId="copy-project-number" />
              <Input
                placeholder={t('auto.proj.autoGeneratedOnSave')}
                value={projectForm.project_number || ''}
                onChange={(e) => setProjectForm(prev => ({ ...prev, project_number: e.target.value }))}
                data-testid="project-number-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                {t('auto.proj.usedOnQrStickersPanelRefs')}
              </p>
            </div>
            <div>
              <CopyableLabel label={t('auto.proj.customerSProjectNumber')} value={projectForm.customer_project_number} testId="copy-customer-project-number" />
              <Input
                placeholder={t('auto.proj.theClientSOwnReference')}
                value={projectForm.customer_project_number || ''}
                onChange={(e) => setProjectForm(prev => ({ ...prev, customer_project_number: e.target.value }))}
                data-testid="customer-project-number-input"
              />
            </div>
          </div>

          <div>
            <CopyableLabel label={t('auto.proj.projectName')} value={projectForm.name} testId="copy-project-name" />
            <Input
              placeholder={t('auto.proj.projectName2')}
              value={projectForm.name}
              onChange={(e) => setProjectForm(prev => ({ ...prev, name: e.target.value}))}
              required
            />
          </div>

          <div>
            <CopyableLabel label={t('auto.proj.description')} value={projectForm.description} testId="copy-project-description" />
            <Input
              placeholder={t('auto.proj.projectDescription')}
              value={projectForm.description || ''}
              onChange={(e) => setProjectForm(prev => ({ ...prev, description: e.target.value}))}
            />
          </div>

          <div>
            <CopyableLabel label={t('auto.proj.webUrl')} value={projectForm.url} testId="copy-project-url" />
            <Input
              placeholder="https://project-portal.example.com"
              value={projectForm.url || ''}
              onChange={(e) => setProjectForm(prev => ({ ...prev, url: e.target.value}))}
              data-testid="project-url-input"
            />
          </div>

          <div>
            <Label>{t('auto.proj.customer3')}</Label>
            <Select 
              value={projectForm.customer_id}
              onValueChange={(value) => setProjectForm(prev => ({ ...prev, customer_id: value}))}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('auto.proj.selectCustomer')} />
              </SelectTrigger>
              <SelectContent>
                {customers.map((customer) => (
                  <SelectItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{t('auto.proj.projectOwnerManagerAdmin')}</Label>
            <Select 
              value={projectForm.owner_id || ''}
              onValueChange={(value) => setProjectForm(prev => ({ ...prev, owner_id: value}))}
            >
              <SelectTrigger data-testid="project-owner-select">
                <SelectValue placeholder={t('auto.proj.selectProjectOwner')} />
              </SelectTrigger>
              <SelectContent>
                {employees
                  .filter(emp => {
                    const user = emp.user_id ? employees.find(e => e.user_id === emp.user_id) : null;
                    return emp.role === 'admin' || emp.role === 'manager';
                  })
                  .map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.role})
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>{t('auto.proj.startDate')}</Label>
              <Input
                type="date"
                value={projectForm.start_date ? (typeof projectForm.start_date === 'string' ? projectForm.start_date.split('T')[0] : new Date(projectForm.start_date).toISOString().split('T')[0]) : ''}
                onChange={(e) => setProjectForm(prev => ({ ...prev, start_date: e.target.value}))}
                required
              />
            </div>
            <div>
              <Label>{t('auto.proj.plannedEndDate')}</Label>
              <Input
                type="date"
                value={projectForm.planned_end_date ? (typeof projectForm.planned_end_date === 'string' ? projectForm.planned_end_date.split('T')[0] : new Date(projectForm.planned_end_date).toISOString().split('T')[0]) : ''}
                onChange={(e) => setProjectForm(prev => ({ ...prev, planned_end_date: e.target.value || null}))}
              />
            </div>
            <div>
              <Label>{t('auto.proj.actualEndDate')}</Label>
              <Input
                type="date"
                value={projectForm.actual_end_date ? (typeof projectForm.actual_end_date === 'string' ? projectForm.actual_end_date.split('T')[0] : new Date(projectForm.actual_end_date).toISOString().split('T')[0]) : ''}
                onChange={(e) => setProjectForm(prev => ({ ...prev, actual_end_date: e.target.value || null}))}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="location" className="space-y-4 mt-4">
          <div>
            <div className="flex items-center gap-1.5">
              <CopyableLabel label={t('auto.proj.siteAddress2')} value={projectForm.site_address} testId="copy-project-site-address" />
              <AddressActions
                parts={[projectForm.site_address, projectForm.site_postal_code, projectForm.site_city, projectForm.site_state, projectForm.site_country]}
                label={t('auto.proj.fullSiteAddress')} testId="copy-project-site-address-full" />
              <span className="text-[11px] text-slate-400">{t('auto.proj.copyTheFullAddressOrOpen')}</span>
            </div>
            <Input
              placeholder={t('auto.proj.streetAddress')}
              value={projectForm.site_address || ''}
              onChange={(e) => setProjectForm(prev => ({ ...prev, site_address: e.target.value}))}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t('auto.proj.city')}</Label>
              <Input
                placeholder={t('auto.proj.city')}
                value={projectForm.site_city || ''}
                onChange={(e) => setProjectForm(prev => ({ ...prev, site_city: e.target.value}))}
              />
            </div>
            <div>
              <Label>{t('auto.proj.stateProvince')}</Label>
              <Input
                placeholder={t('auto.proj.stateProvince')}
                value={projectForm.site_state || ''}
                onChange={(e) => setProjectForm(prev => ({ ...prev, site_state: e.target.value}))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t('auto.proj.postalCode')}</Label>
              <Input
                placeholder={t('auto.proj.postalCode')}
                value={projectForm.site_postal_code || ''}
                onChange={(e) => setProjectForm(prev => ({ ...prev, site_postal_code: e.target.value}))}
              />
            </div>
            <div>
              <Label>{t('auto.proj.country')}</Label>
              <Input
                placeholder={t('auto.proj.country')}
                value={projectForm.site_country || ''}
                onChange={(e) => setProjectForm(prev => ({ ...prev, site_country: e.target.value}))}
              />
            </div>
          </div>

          {/* End-client subsection — used by support letters & contracts
              where the actual installation owner is separate from the
              direct (invoicing) customer. */}
          <div className="border-t pt-4 mt-2" data-testid="end-client-section">
            <div className="text-sm font-semibold text-slate-700 mb-2">{t('auto.proj.endClientFinalBeneficiary')}</div>
            <p className="text-xs text-slate-500 mb-3">
              The party who owns the installation / site. Leave blank when
              the customer above is also the end client.
            </p>
            {(() => {
              const ec = projectForm.end_client || {};
              const setEc = (patch) => setProjectForm(prev => ({
                ...prev,
                end_client: { ...(prev.end_client || {}), ...patch },
              }));
              return (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>{t('auto.proj.endClientName')}</Label>
                      <Input placeholder={t('auto.proj.companyName')}
                        value={ec.name || ''}
                        onChange={(e) => setEc({ name: e.target.value })}
                        data-testid="end-client-name" />
                    </div>
                    <div>
                      <Label>{t('auto.proj.representative')}</Label>
                      <Input placeholder={t('auto.proj.contactPerson')}
                        value={ec.representative_name || ''}
                        onChange={(e) => setEc({ representative_name: e.target.value })}
                        data-testid="end-client-rep-name" />
                    </div>
                    <div>
                      <Label>{t('auto.proj.representativePosition')}</Label>
                      <Input placeholder="e.g. Site Manager"
                        value={ec.representative_position || ''}
                        onChange={(e) => setEc({ representative_position: e.target.value })} />
                    </div>
                    <div>
                      <Label>{t('auto.proj.phone')}</Label>
                      <Input placeholder="+48 …"
                        value={ec.phone || ''}
                        onChange={(e) => setEc({ phone: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>{t('auto.proj.email')}</Label>
                      <Input type="email" placeholder="contact@endclient.com"
                        value={ec.email || ''}
                        onChange={(e) => setEc({ email: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>{t('auto.proj.address')}</Label>
                    <Input placeholder={t('auto.proj.streetAddress')}
                      value={ec.address || ''}
                      onChange={(e) => setEc({ address: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>{t('auto.proj.city')}</Label>
                      <Input placeholder={t('auto.proj.city')}
                        value={ec.city || ''}
                        onChange={(e) => setEc({ city: e.target.value })} />
                    </div>
                    <div>
                      <Label>{t('auto.proj.stateProvince')}</Label>
                      <Input placeholder={t('auto.proj.stateProvince')}
                        value={ec.state || ''}
                        onChange={(e) => setEc({ state: e.target.value })}
                        data-testid="end-client-state" />
                    </div>
                    <div>
                      <Label>{t('auto.proj.postalCode')}</Label>
                      <Input placeholder={t('auto.proj.postalCode')}
                        value={ec.postal_code || ''}
                        onChange={(e) => setEc({ postal_code: e.target.value })} />
                    </div>
                    <div>
                      <Label>{t('auto.proj.country')}</Label>
                      <Input placeholder={t('auto.proj.country')}
                        value={ec.country || ''}
                        onChange={(e) => setEc({ country: e.target.value })} />
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </TabsContent>

        {seeContacts && (
        <TabsContent value="contacts" className="space-y-4 mt-4 max-h-96 overflow-y-auto">
          {(() => {
            // Feed the customer's saved contacts into every contact field
            // so the autofill combobox suggests them when a name is typed.
            const selectedCustomer = (customers || []).find(c => c.id === projectForm.customer_id);
            const customerContacts = selectedCustomer?.contacts || [];
            const hint = selectedCustomer && customerContacts.length > 0
              ? `Autofill available from ${customerContacts.length} contact${customerContacts.length === 1 ? '' : 's'} at ${selectedCustomer.name}.`
              : selectedCustomer
                ? `No contacts yet on ${selectedCustomer.name}. Add some in Customers → Contacts to enable autofill.`
                : 'Pick a customer on the General tab to enable contact autofill.';
            return (<>
              <p className="text-xs text-slate-500 italic" data-testid="contact-autofill-hint">{hint}</p>
              <ContactPersonFields
                label={t('auto.proj.customerContactPerson')}
                contact={getContact('customer_contact')}
                onChange={(contact) => handleContactChange('customer_contact', contact)}
                customerContacts={customerContacts}
              />
              <ContactPersonFields
                label={t('auto.proj.siteContactPerson')}
                contact={getContact('site_contact')}
                onChange={(contact) => handleContactChange('site_contact', contact)}
                customerContacts={customerContacts}
              />
              <ContactPersonFields
                label={t('auto.proj.mechanicalSupervisor')}
                contact={getContact('mechanical_supervisor')}
                onChange={(contact) => handleContactChange('mechanical_supervisor', contact)}
                customerContacts={customerContacts}
              />
              <ContactPersonFields
                label={t('auto.proj.electricalSupervisor')}
                contact={getContact('electrical_supervisor')}
                onChange={(contact) => handleContactChange('electrical_supervisor', contact)}
                customerContacts={customerContacts}
              />
            </>);
          })()}
        </TabsContent>
        )}

        {seeTeam && (
        <TabsContent value="team" className="space-y-4 mt-4">
          {/* Budget Configuration */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">{t('auto.proj.budgetConfiguration')}</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">{t('auto.proj.budgetType')}</Label>
                <Select 
                  value={projectForm.budget_type || 'hourly'}
                  onValueChange={(value) => setProjectForm(prev => ({ ...prev, budget_type: value}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">{t('auto.proj.hourlyRate')}</SelectItem>
                    <SelectItem value="fixed">{t('auto.proj.fixedPrice')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {projectForm.budget_type === 'fixed' && (
                <div>
                  <Label className="text-xs">{t('auto.proj.budgetAmount')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={projectForm.budget_amount || ''}
                    onChange={(e) => setProjectForm(prev => ({ ...prev, budget_amount: e.target.value ? parseFloat(e.target.value) : ''}))}
                  />
                </div>
              )}
              <div>
                <Label className="text-xs">{t('auto.proj.budgetCurrency')}</Label>
                <Select 
                  value={projectForm.budget_currency || 'PLN'}
                  onValueChange={(value) => setProjectForm(prev => ({ ...prev, budget_currency: value}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Project Hourly Rate (for revenue calculation) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t('auto.proj.projectHourlyRateForBilling')}</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={projectForm.hourly_rate || ''}
                onChange={(e) => setProjectForm(prev => ({ ...prev, hourly_rate: e.target.value}))}
              />
            </div>
            <div>
              <Label>{t('auto.proj.rateCurrency')}</Label>
              <Select 
                value={projectForm.currency || 'EUR'}
                onValueChange={(value) => setProjectForm(prev => ({ ...prev, currency: value}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectCurrency')} />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex justify-between items-center mb-3">
              <Label>{t('auto.proj.teamMembers')}</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setBulkAddOpen(true)} data-testid="btn-bulkaddteam">
                  <User className="w-4 h-4 mr-1" /> {t('auto.proj.bulkAdd')}
                </Button>
                <Button type="button" size="sm" onClick={addTeamMember} data-testid="btn-addteammember">
                  <Plus className="w-4 h-4 mr-1" /> {t('auto.proj.addMember')}
                </Button>
              </div>
            </div>

            <div className="space-y-3 max-h-64 overflow-y-auto">
              {(projectForm.team_members || []).map((member, index) => {
                // Compute overlapping assignments on OTHER active
                // projects. Non-blocking — the warning banner is
                // purely informational so managers can consciously
                // over-book if a shift split makes it feasible.
                const conflicts = member?.employee_id
                  ? findAssignmentConflicts(projectForm, member, otherProjects)
                  : [];
                return (
                <Card key={index} className={conflicts.length ? 'border-amber-400' : ''}>
                  <CardContent className="pt-4">
                    {conflicts.length > 0 && (
                      <div
                        className="mb-3 flex gap-2 items-start rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                        data-testid={`team-conflict-warning-${index}`}
                      >
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                        <div className="flex-1">
                          <div className="font-medium">{t('auto.proj.assignmentOverlapsAnotherProject')}</div>
                          <ul className="mt-1 space-y-0.5 text-xs">
                            {conflicts.map((c) => (
                              <li key={c.project_id}>
                                <span className="font-semibold">{c.project_name}</span>
                                {c.role ? ` · ${c.role}` : ''} —{' '}
                                {c.intervals.map((iv, ii) => {
                                  const fmt = (d) => (d ? d.toISOString().slice(0, 10) : '…');
                                  return (
                                    <span key={ii}>
                                      {ii > 0 && ', '}
                                      {fmt(iv.start)}→{fmt(iv.end)}
                                    </span>
                                  );
                                })}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    <div className="flex gap-3 flex-wrap">
                      <div className="flex-1 min-w-[150px]">
                        <Label className="text-xs">{t('auto.proj.employee')}</Label>
                        <Select 
                          value={member.employee_id}
                          onValueChange={(value) => updateTeamMember(index, 'employee_id', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('auto.proj.selectEmployee2')} />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map((emp) => (
                              <SelectItem key={emp.id} value={emp.id}>
                                {emp.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1 min-w-[130px]">
                        <Label className="text-xs">{t('auto.proj.role')}</Label>
                        {/* Free-text Input with datalist suggestions.
                            Switched from Radix Select because it resets
                            values that don't match any mounted SelectItem
                            (SelectItems only register when the popover is
                            opened) — that swallowed auto-filled roles
                            like "Engineer" that aren't in the canonical
                            projectRoles list. */}
                        <Input
                          list={`project-roles-${index}`}
                          value={member.role || ''}
                          onChange={(e) => updateTeamMember(index, 'role', e.target.value)}
                          placeholder={t('auto.proj.selectOrTypeRole')}
                          data-testid={`team-member-role-${index}`}
                        />
                        <datalist id={`project-roles-${index}`}>
                          {projectRoles.map((role) => (
                            <option key={role} value={role} />
                          ))}
                        </datalist>
                      </div>
                      {isAdminOrManager && (
                        <>
                          <div className="w-24">
                            <Label className="text-xs">{t('auto.proj.hourlyRate')}</Label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={member.hourly_rate || ''}
                              onChange={(e) => updateTeamMember(index, 'hourly_rate', e.target.value ? parseFloat(e.target.value) : '')}
                            />
                          </div>
                          <div className="w-20">
                            <Label className="text-xs">{t('auto.proj.currency')}</Label>
                            <Select 
                              value={member.currency || 'EUR'}
                              onValueChange={(value) => updateTeamMember(index, 'currency', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {currencies.map((c) => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}
                      {isEdit && isAdminOrManager && member.employee_id && projectForm.id && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-5"
                          title={t('auto.proj.rateHistory')}
                          onClick={() => setRateHistoryFor({
                            index,
                            employee_id: member.employee_id,
                            employee_name: employees?.find?.((e) => e.id === member.employee_id)?.name || 'Team member',
                          })}
                          data-testid={`team-rate-history-btn-${index}`}
                        >
                          <History className="w-4 h-4" />
                        </Button>
                      )}
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={() => removeTeamMember(index)}
                        className="mt-5"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    {/* Assignment window — whole-project vs one-or-more
                        explicit spans. Feeds the Unified Overview so an
                        employee doesn't look "double-booked" when they're
                        only on-site for part of a project. */}
                    <div className="mt-3 pt-3 border-t space-y-2">
                      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`team-whole-duration-wrap-${index}`}>
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={member.whole_duration !== false}
                          onChange={(e) => updateTeamMember(index, 'whole_duration', e.target.checked)}
                          data-testid={`team-whole-duration-${index}`}
                        />
                        <span className="text-slate-700">{t('auto.proj.assignedForTheWholeProjectDuration')}</span>
                      </label>
                      {member.whole_duration === false && (
                        <div className="space-y-2 pl-6">
                          {((member.spans && member.spans.length ? member.spans : [{ from_date: '', to_date: '' }])).map((span, sIdx) => (
                            <div key={sIdx} className="flex items-center gap-2 flex-wrap" data-testid={`team-span-${index}-${sIdx}`}>
                              <div>
                                <Label className="text-xs">{t('auto.proj.from')}</Label>
                                <Input
                                  type="date"
                                  value={span.from_date || ''}
                                  onChange={(e) => {
                                    const spans = [...(member.spans || [])];
                                    while (spans.length <= sIdx) spans.push({ from_date: '', to_date: '' });
                                    spans[sIdx] = { ...spans[sIdx], from_date: e.target.value };
                                    updateTeamMember(index, 'spans', spans);
                                  }}
                                  data-testid={`team-span-${index}-${sIdx}-from`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">{t('auto.proj.to')}</Label>
                                <Input
                                  type="date"
                                  value={span.to_date || ''}
                                  onChange={(e) => {
                                    const spans = [...(member.spans || [])];
                                    while (spans.length <= sIdx) spans.push({ from_date: '', to_date: '' });
                                    spans[sIdx] = { ...spans[sIdx], to_date: e.target.value };
                                    updateTeamMember(index, 'spans', spans);
                                  }}
                                  data-testid={`team-span-${index}-${sIdx}-to`}
                                />
                              </div>
                              {(member.spans && member.spans.length > 1) && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="mt-5"
                                  title={t('auto.proj.removeSpan')}
                                  onClick={() => {
                                    const spans = [...(member.spans || [])];
                                    spans.splice(sIdx, 1);
                                    updateTeamMember(index, 'spans', spans);
                                  }}
                                  data-testid={`team-span-${index}-${sIdx}-remove`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const spans = [...(member.spans || [{ from_date: '', to_date: '' }])];
                              spans.push({ from_date: '', to_date: '' });
                              updateTeamMember(index, 'spans', spans);
                            }}
                            data-testid={`team-span-${index}-add`}
                          >
                            <Plus className="w-3 h-3 mr-1" /> {t('auto.proj.addSpan')}
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );})}
            </div>
          </div>
        </TabsContent>
        )}
      </Tabs>

      <FloatingSaveButton
        type="submit"
        label={isEdit ? t('auto.proj.updateProject') : t('auto.proj.createProject')}
        testId="project-floating-save-btn"
      />

      <Button type="submit" className="w-full mt-4">
        {isEdit ? t('auto.proj.updateProject') : t('auto.proj.createProject')}
      </Button>

      {rateHistoryFor && (
        <TeamMemberRateHistory
          open={!!rateHistoryFor}
          onClose={() => setRateHistoryFor(null)}
          projectId={projectForm.id}
          employeeId={rateHistoryFor.employee_id}
          employeeName={rateHistoryFor.employee_name}
          isAdmin={isAdmin}
          onUpdated={(newRate, newCur, newHistory) => {
            // Sync the visible rate, currency, and history on the row
            // in ONE setState — three separate updateTeamMember calls
            // would race on the same projectForm snapshot and lose
            // two of the three updates.
            const patch = { hourly_rate: newRate };
            if (newCur) patch.currency = newCur;
            if (Array.isArray(newHistory)) patch.rate_history = newHistory;
            patchTeamMember(rateHistoryFor.index, patch);
          }}
        />
      )}

      <BulkAddTeamDialog
        open={bulkAddOpen}
        onOpenChange={setBulkAddOpen}
        employees={employees}
        departments={settings?.departments || []}
        existingTeamMemberIds={(projectForm.team_members || []).map((m) => m.employee_id).filter(Boolean)}
        onAdd={bulkAddMembers}
      />
    </form>
  );
};

export default ProjectForm;
